const PRODUCT_BATCH = 4;
const MAX_PAGES = 5;
const PAGES_FETCH_BATCH = 70;
const SEARCH_DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;
const HEADER_REDIRECTS_PAGE_LIMIT = 20;
const DEBUG_BOOST_HEADER_SEARCH = true;
const FALLBACK_API_SEARCH_URL_HEADER = 'https://services.mybcapps.com/bc-sf-filter/search';

function warnBoostHeaderSearch(...args) {
  if (!DEBUG_BOOST_HEADER_SEARCH) return;
  console.warn('[boost-header-search]', ...args);
}

function getBoostConfig() {
  return window.boostPFSConfig || {};
}

function getBoostSearchBaseUrl() {
  return getBoostConfig().api?.searchUrl || FALLBACK_API_SEARCH_URL_HEADER;
}

function getBoostPagesApiUrl() {
  return `${getBoostSearchBaseUrl()}/pages`;
}

function getBoostRedirectsUrl() {
  return `${getBoostSearchBaseUrl()}/redirects`;
}

function normalizeRedirectTerm(term) {
  return String(term || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function localizeRedirectUrl(redirectTo, rootUrl) {
  if (!redirectTo || typeof redirectTo !== 'string') return redirectTo;

  let parsed;
  try {
    parsed = new URL(redirectTo, window.location.origin);
  } catch {
    return redirectTo;
  }

  const isAbsolute = /^[a-z][a-z0-9+.-]*:/i.test(redirectTo) || redirectTo.startsWith('//');
  if (isAbsolute) {
    const shopDomain = getBoostConfig().shop?.domain;
    let shopHost = '';
    try {
      const shopUrl = getBoostConfig().shop?.url;
      if (shopUrl) shopHost = new URL(shopUrl).hostname;
    } catch {
      // ignore
    }
    if (
      parsed.hostname !== window.location.hostname &&
      parsed.hostname !== shopDomain &&
      parsed.hostname !== shopHost
    ) {
      return redirectTo;
    }
  }

  const segments = parsed.pathname.split('/').filter(Boolean);
  const first = segments[0]?.toLowerCase();
  const locales = getBoostConfig().general?.published_locales || {};
  if (
    first &&
    (Object.keys(locales).some((code) => code.toLowerCase() === first) ||
      /^[a-z]{2}-[a-z]{2}$/.test(first))
  ) {
    segments.shift();
  }

  const root = rootUrl || window.theme?.routes?.root_url || '/';
  const prefix = !root || root === '/' ? '' : `/${String(root).replace(/^\/+|\/+$/g, '')}`;
  const path = segments.length ? `/${segments.join('/')}` : '';

  return `${prefix}${path || (prefix ? '' : '/')}${parsed.search}${parsed.hash}`;
}

class BoostHeaderSearch {
  constructor(section) {
    this.section = section;
    this.header = section.querySelector('header.header');
    this.input = section.querySelector('.boost-input input[name="q"]');
    this.boostInput = this.input?.closest('.boost-input') || section.querySelector('.boost-input');
    this.form = section.querySelector('[data-boost-search-form]');
    this.submitButton = section.querySelector('[data-boost-search-submit]');
    this.clearButton = section.querySelector('[data-boost-search-clear]');
    this.closeButton = section.querySelector('[data-boost-search-close]');
    this.drawers = section.querySelectorAll('[data-boost-drawer]');

    if (!this.header || !this.input) {
      warnBoostHeaderSearch('Init skipped for section', {
        sectionId: section.id,
        hasHeader: Boolean(this.header),
        hasInput: Boolean(this.input),
        hasForm: Boolean(this.form),
        drawersCount: this.drawers.length,
      });
      return;
    }

    this.searchToggles = section.querySelectorAll('[data-boost-search-toggle]');
    this.isOpen = false;
    this.transition = null;
    this.searchAbort = null;
    this.searchRequestId = 0;
    this.searchTimer = null;
    this.productObservers = new Map();
    this.isInitialized = true;
    this.redirectsMap = null;
    this.redirectsPromise = null;

    this.onSearchToggleClick = this.onSearchToggleClick.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onSubmitClick = this.onSubmitClick.bind(this);
    this.onDrawerClick = this.onDrawerClick.bind(this);
    this.onClearClick = this.onClearClick.bind(this);
    this.onCloseClick = this.onCloseClick.bind(this);

    this.shop = getBoostConfig().shop?.domain || '';
    this.currency =
      getBoostConfig().general?.current_currency || window.Shopify?.currency?.active || '';
    this.country = window.Shopify?.country || '';

    this.searchToggles.forEach((button) => {
      button.addEventListener('click', this.onSearchToggleClick);
    });

    this.input.addEventListener('input', this.onInput);
    this.form?.addEventListener('submit', this.onSubmit);
    this.submitButton?.addEventListener('click', this.onSubmitClick);
    this.clearButton?.addEventListener('click', this.onClearClick);
    this.closeButton?.addEventListener('click', this.onCloseClick);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);

    this.drawers.forEach((drawer) => {
      this.initDrawer(drawer);
      drawer.addEventListener('click', this.onDrawerClick);
    });

    this.prefetchRedirects();
  }

  getShopDomain() {
    return getBoostConfig().shop?.domain || this.shop || '';
  }

  get motionReduced() {
    return window.theme?.config?.motionReduced ?? false;
  }

  get animationDuration() {
    return this.motionReduced ? 0 : 0.6;
  }

  initDrawer(drawer) {
    this.ensureResultsMarkup(drawer);
    this.refreshDrawerRefs(drawer);

    drawer._config = {
      searchUrl: drawer.dataset.searchUrl || '/search',
      rootUrl: drawer.dataset.rootUrl || '/',
      emptyMessage: drawer.dataset.emptyMessage || 'Keine Produkte gefunden',
      viewAllLabel: drawer.dataset.viewAllLabel || 'Zu allen {count} Ergebnissen',
      pageTitle: drawer.dataset.pageTitle || 'Wissensdatenbank',
      serviceTitle: drawer.dataset.serviceTitle || 'Dienstleistungen',
      productTitle: drawer.dataset.productTitle || 'Produkte',
      kiGeneratedLabel: drawer.dataset.kiGeneratedLabel || '',
    };

    drawer._productState = {
      query: '',
      page: 0,
      total: 0,
      hasMore: false,
      loading: false,
    };

    this.setupProductObserver(drawer);
  }

  refreshDrawerRefs(drawer) {
    drawer._refs = {
      main: drawer.querySelector('[data-boost-main]') || drawer.querySelector('.boost-drawer__main'),
      defaultView: drawer.querySelector('[data-boost-default]'),
      results: drawer.querySelector('[data-boost-results]'),
      productsSection: drawer.querySelector('[data-boost-products-section]'),
      productsList: drawer.querySelector('[data-boost-results] [data-boost-products-list]'),
      productsLink: drawer.querySelector('[data-boost-products-link]'),
      pagesSection: drawer.querySelector('[data-boost-pages-section]'),
      pagesList: drawer.querySelector('[data-boost-results] [data-boost-pages-list]'),
      pagesLink: drawer.querySelector('[data-boost-pages-link]'),
      servicesSection: drawer.querySelector('[data-boost-services-section]'),
      servicesList: drawer.querySelector('[data-boost-results] [data-boost-services-list]'),
      servicesLink: drawer.querySelector('[data-boost-services-link]'),
      emptyMessage: drawer.querySelector('[data-boost-results] [data-boost-empty-message]'),
      sentinel: drawer.querySelector('[data-boost-results] [data-boost-products-sentinel]'),
    };

    return drawer._refs;
  }

  prepareDrawer(drawer) {
    this.ensureResultsMarkup(drawer);
    return this.refreshDrawerRefs(drawer);
  }

  isDrawerReady(drawer) {
    const refs = this.prepareDrawer(drawer);
    return Boolean(refs.results && refs.productsList);
  }

  setEmptyMessage(refs, { visible = false, text = '' } = {}) {
    const messages = refs.results?.querySelectorAll('[data-boost-empty-message]') || [];

    messages.forEach((message) => {
      if (visible) {
        message.textContent = text;
        message.removeAttribute('hidden');
        message.classList.remove('is-hidden');
      } else {
        message.textContent = '';
        message.setAttribute('hidden', '');
        message.classList.add('is-hidden');
      }
    });
  }

  ensureResultsMarkup(drawer) {
    const main = drawer.querySelector('[data-boost-main]') || drawer.querySelector('.boost-drawer__main');
    if (!main) {
      warnBoostHeaderSearch('No drawer main container found', { drawerId: drawer.id });
      return;
    }

    let results = main.querySelector('[data-boost-results]');
    if (
      results?.querySelector('[data-boost-products-list]') &&
      results?.querySelector('[data-boost-pages-list]') &&
      results?.querySelector('[data-boost-services-list]')
    ) {
      return;
    }

    if (results) {
      results.remove();
      results = null;
    } else {
    }

    const config = {
      searchUrl: drawer.dataset.searchUrl || '/search',
      emptyMessage: drawer.dataset.emptyMessage || 'Keine Produkte gefunden',
      viewAllLabel: drawer.dataset.viewAllLabel || 'Zu allen {count} Ergebnissen',
      productTitle: drawer.dataset.productTitle || 'Produkte',
      pageTitle: drawer.dataset.pageTitle || 'Wissensdatenbank',
      serviceTitle: drawer.dataset.serviceTitle || 'Dienstleistungen',
      kiGeneratedLabel: drawer.dataset.kiGeneratedLabel || '',
    };

    const resultsNode = document.createElement('div');
    resultsNode.className = 'boost-drawer__results';
    resultsNode.setAttribute('data-boost-results', '');
    resultsNode.hidden = true;
    resultsNode.innerHTML = `
      <div class="boost-drawer__section" data-boost-products-section>
        <div class="boost-drawer__section-header">
          <p class="boost-drawer__section-label">${this.escapeHtml(config.productTitle)}</p>
          <a class="boost-drawer__view-all" data-boost-products-link href="${this.escapeHtml(config.searchUrl)}" hidden>
            ${this.escapeHtml(config.viewAllLabel)}
          </a>
        </div>
        <p class="empty-message boost-drawer__empty-message" data-boost-empty-message hidden></p>
        <ul class="boost-drawer__products" data-boost-products-list role="list"></ul>
        <div class="boost-drawer__sentinel" data-boost-products-sentinel aria-hidden="true" hidden>
          <span class="boost-drawer__loader"></span>
        </div>
      </div>
      <div class="flex boost-drawer__sections">
        <div class="boost-drawer__section boost-drawer__pages" data-boost-pages-section hidden>
          <div class="boost-drawer__section-header">
            <p class="boost-drawer__section-label">${this.escapeHtml(config.pageTitle)}</p>
            <a class="boost-drawer__view-all" data-boost-pages-link href="${this.escapeHtml(config.searchUrl)}" hidden>
              ${this.escapeHtml(config.viewAllLabel)}
            </a>
          </div>
          <ul class="boost-drawer__pages-list" data-boost-pages-list role="list"></ul>
        </div>

        <div class="boost-drawer__section boost-drawer__services" data-boost-services-section hidden>
          <div class="boost-drawer__section-header">
            <p class="boost-drawer__section-label">${this.escapeHtml(config.serviceTitle)}</p>
            <a class="boost-drawer__view-all" data-boost-services-link href="${this.escapeHtml(config.searchUrl)}" hidden>
              ${this.escapeHtml(config.viewAllLabel)}
            </a>
          </div>
          <ul class="boost-drawer__services-list" data-boost-services-list role="list"></ul>
        </div>
      </div>
    `;

    main.appendChild(resultsNode);
  }

  setupProductObserver(drawer) {
    const { main, sentinel } = drawer._refs;
    if (!main || !sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.loadMoreProducts(drawer);
        }
      },
      {
        root: main,
        rootMargin: '120px',
      }
    );

    observer.observe(sentinel);
    this.productObservers.set(drawer, observer);
  }

  onSearchToggleClick(event) {
    event.preventDefault();
    if (this.isOpen) {
      this.deactivate();
      return;
    }
    this.activate();
  }

  onSubmit(event) {
    event.preventDefault();
    this.redirectToSearchPage();
  }

  onSubmitClick(event) {
    event.preventDefault();
    this.redirectToSearchPage();
  }

  onDrawerClick(event) {
    const productLink = event.target.closest('[data-boost-product-id]');
    if (!productLink || !event.currentTarget.contains(productLink)) return;

    window.boostAnalytics?.trackProduct({
      productId: productLink.dataset.boostProductId,
      requestId: productLink.dataset.boostRequestId,
      queryString: productLink.dataset.boostQuery
        ? new URLSearchParams({ q: productLink.dataset.boostQuery }).toString()
        : '',
      action: 'suggest',
    });
  }

  onClearClick(event) {
    event.preventDefault();
    this.clearInput();
  }

  onCloseClick(event) {
    event.preventDefault();
    this.deactivate();
  }

  clearInput() {
    if (!this.input) return;
    if (this.input.value === '') this.deactivate();

    this.input.value = '';
    this.input.focus();
    this.searchAbort?.abort();
    clearTimeout(this.searchTimer);
    this.resetAllDrawers();
  }

  prefetchRedirects() {
    this.loadRedirects();
  }

  buildRedirectsParams(page) {
    const params = new URLSearchParams({
      shop: this.shop,
      limit: String(HEADER_REDIRECTS_PAGE_LIMIT),
      page: String(page),
    });
    const locale = getBoostConfig().general?.current_locale;
    if (locale) {
      params.set('locale', locale);
    }
    return params;
  }

  async fetchRedirectsPage(page) {
    const response = await fetch(`${getBoostRedirectsUrl()}?${this.buildRedirectsParams(page)}`);
    if (!response.ok) {
      throw new Error(`Boost redirects API responded with ${response.status}`);
    }
    return response.json();
  }

  addRedirectsToMap(map, items) {
    for (const item of items || []) {
      const redirectTo = item.redirect_to;
      if (!redirectTo) continue;

      for (const term of item.terms || []) {
        const key = normalizeRedirectTerm(term);
        if (key) {
          map.set(key, redirectTo);
        }
      }
    }
  }

  async loadRedirects() {
    if (this.redirectsMap) return this.redirectsMap;
    if (this.redirectsPromise) return this.redirectsPromise;

    this.redirectsPromise = (async () => {
      try {
        if (!this.shop) return new Map();

        const firstData = await this.fetchRedirectsPage(1);
        const map = new Map();
        this.addRedirectsToMap(map, firstData.data);

        const total = firstData.total ?? map.size;
        const totalPages = Math.ceil(total / HEADER_REDIRECTS_PAGE_LIMIT);

        if (totalPages > 1) {
          const rest = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, index) => this.fetchRedirectsPage(index + 2))
          );

          for (const pageData of rest) {
            this.addRedirectsToMap(map, pageData.data);
          }
        }

        this.redirectsMap = map;
        return map;
      } catch (error) {
        warnBoostHeaderSearch('Redirects load failed', error);
        this.redirectsPromise = null;
        return new Map();
      }
    })();

    return this.redirectsPromise;
  }

  getRootUrl() {
    return (
      this.drawers[0]?._config?.rootUrl ||
      window.theme?.routes?.root_url ||
      '/'
    );
  }

  async getRedirectUrlForQuery(query) {
    const key = normalizeRedirectTerm(query);
    if (!key) return null;

    const redirects = await this.loadRedirects();
    const redirectTo = redirects.get(key);
    if (!redirectTo) return null;

    return localizeRedirectUrl(redirectTo, this.getRootUrl());
  }

  async redirectToSearchPage() {
    const query = this.input?.value.trim() || '';

    if (query) {
      const redirectUrl = await this.getRedirectUrlForQuery(query);
      if (redirectUrl) {
        window.location.href = redirectUrl;
        return;
      }
    }

    const baseUrl =
      this.form?.getAttribute('action') ||
      this.drawers[0]?._config?.searchUrl ||
      '/search';
    const url = new URL(baseUrl, window.location.origin);

    if (query) {
      url.searchParams.set('q', query);
    } else {
      url.searchParams.delete('q');
    }

    window.location.href = url.toString();
  }

  onInput() {
    const query = this.input?.value.trim() || '';

    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.runSearch(query);
    }, SEARCH_DEBOUNCE_MS);
  }

  runSearch(query, immediate = false) {
    if (!query || query.length < MIN_QUERY_LENGTH) {
      this.resetAllDrawers();
      return;
    }

    if (!this.isOpen) {
      this.activate();
    }

    this.drawers.forEach((drawer) => {
      this.prepareSearchRequest(drawer);
    });

    if (immediate) {
      clearTimeout(this.searchTimer);
    }

    this.fetchSearchResults(query);
  }

  updateTopHeaderPosition() {
    if (window.innerWidth >= 1200 || !this.boostInput) {
      document.documentElement.style.removeProperty('--top-header-position');
      return;
    }

    const bottom = this.boostInput.getBoundingClientRect().bottom;
    document.documentElement.style.setProperty('--top-header-position', `${bottom}px`);
  }

  clearTopHeaderPosition() {
    document.documentElement.style.removeProperty('--top-header-position');
  }

  async activate() {
    if (this.isOpen) {
      this.input?.focus();
      return;
    }

    this.header.classList.add('boost-active');
    this.isOpen = true;
    this.input?.focus();

    document.body.classList.add('boost-search-open', 'has-modal-open');
    this.updateTopHeaderPosition();

    const animations = [...this.drawers].map((drawer) => {
      const shell = drawer.querySelector('.boost-drawer__shell');
      drawer.hidden = false;
      drawer.classList.add('is-open');

      if (!shell) {
        return Promise.resolve();
      }

      return this.animateIn(shell);
    });

    this.transition = Promise.all(animations);
    await this.transition;
    this.transition = null;
  }

  async deactivate() {
    if (!this.isOpen) {
      return;
    }

    this.isOpen = false;
    this.clearTopHeaderPosition();

    if (this.input) {
      this.input.value = '';
    }

    this.searchAbort?.abort();
    clearTimeout(this.searchTimer);
    this.resetAllDrawers();

    document.body.classList.remove('boost-search-open', 'has-modal-open');

    const animations = [...this.drawers].map(async (drawer) => {
      const shell = drawer.querySelector('.boost-drawer__shell');

      if (shell) {
        await this.animateOut(shell);
      }

      drawer.classList.remove('is-open', 'is-loading');
      this.header.classList.remove('boost-active');
      drawer.hidden = true;
    });

    this.transition = Promise.all(animations);
    await this.transition;
    this.transition = null;
  }

  resetAllDrawers() {
    this.drawers.forEach((drawer) => {
      this.resetProductState(drawer);
      this.showDefaultMode(drawer);
    });
  }

  showDefaultMode(drawer) {
    const refs = this.prepareDrawer(drawer);
    refs.defaultView?.removeAttribute('hidden');
    refs.results?.setAttribute('hidden', '');
    drawer.classList.remove('is-loading');
  }

  showResultsMode(drawer) {
    const refs = this.prepareDrawer(drawer);
    refs.defaultView?.setAttribute('hidden', '');
    refs.results?.removeAttribute('hidden');
  }

  prepareSearchRequest(drawer) {
    const refs = this.prepareDrawer(drawer);
    if (!refs) return;

    const {
      pagesList,
      pagesLink,
      pagesSection,
      servicesList,
      servicesLink,
      servicesSection,
      productsLink,
      sentinel,
    } = refs;

    if (pagesList) pagesList.innerHTML = '';
    if (servicesList) servicesList.innerHTML = '';
    pagesLink?.setAttribute('hidden', '');
    pagesSection?.setAttribute('hidden', '');
    servicesLink?.setAttribute('hidden', '');
    servicesSection?.setAttribute('hidden', '');
    productsLink?.setAttribute('hidden', '');
    sentinel?.setAttribute('hidden', '');
  }

  resetProductState(drawer) {
    drawer._productState = {
      query: '',
      page: 0,
      total: 0,
      hasMore: false,
      loading: false,
    };

    const refs = this.prepareDrawer(drawer);
    if (!refs) return;

    const {
      productsList,
      pagesList,
      servicesList,
      productsLink,
      pagesLink,
      pagesSection,
      servicesLink,
      servicesSection,
      sentinel,
    } = refs;

    if (productsList) productsList.innerHTML = '';
    if (pagesList) pagesList.innerHTML = '';
    if (servicesList) servicesList.innerHTML = '';
    this.setEmptyMessage(refs, { visible: false });
    productsLink?.setAttribute('hidden', '');
    pagesLink?.setAttribute('hidden', '');
    pagesSection?.setAttribute('hidden', '');
    servicesLink?.setAttribute('hidden', '');
    servicesSection?.setAttribute('hidden', '');
    sentinel?.setAttribute('hidden', '');
  }

  buildBaseParams(query) {
    const params = new URLSearchParams();
    params.set('shop', this.getShopDomain());
    params.set('q', query);
    window.boostAnalytics?.appendEventType(params, 'suggest');

    if (this.currency) {
      params.set('currency', this.currency);
    }

    const rate = window.Shopify?.currency?.rate;
    params.set('currency_rate', rate != null && rate !== '' ? String(rate) : '1');

    if (this.country) {
      params.set('country', this.country);
    }

    return params;
  }

  buildProductParams(query, page) {
    const params = this.buildBaseParams(query);
    params.set('limit', String(PRODUCT_BATCH));
    params.set('page', String(page));
    return params;
  }

  buildPagesParams(query, page = 1) {
    const params = new URLSearchParams();
    params.set('shop', this.getShopDomain());
    params.set('q', query);
    window.boostAnalytics?.appendEventType(params, 'filter');
    params.set('limit', String(PAGES_FETCH_BATCH));
    params.set('page', String(page));

    if (this.currency) {
      params.set('currency', this.currency);
    }

    const rate = window.Shopify?.currency?.rate;
    params.set('currency_rate', rate != null && rate !== '' ? String(rate) : '1');

    if (this.country) {
      params.set('country', this.country);
    }

    const locale = getBoostConfig().general?.current_locale;
    if (locale) {
      params.set('locale', locale);
    }

    return params;
  }

  getBoostPageEntries() {
    return (window.boostPages || []).filter(
      (entry) => entry?.id != null && (entry.type === 'page' || entry.type === 'service')
    );
  }

  async fetchBoostPages(query, signal) {
    const matched = [];
    const seenIds = new Set();
    const apiPages = [];
    let apiPage = 1;
    let apiTotal = Infinity;

    while (apiPages.length < apiTotal) {
      const pagesUrl = `${getBoostPagesApiUrl()}?${this.buildPagesParams(query, apiPage)}`;
      const response = await fetch(pagesUrl, { signal });

      if (!response.ok) {
        throw new Error(`Boost pages API responded with ${response.status}`);
      }

      const data = await response.json();
      const pages = data.pages || [];
      apiPages.push(...pages);

      this.filterPages(pages).forEach((page) => {
        const pageId = Number(page.id);
        if (seenIds.has(pageId)) {
          return;
        }

        seenIds.add(pageId);
        matched.push(page);
      });

      apiTotal = data.total_page ?? apiPages.length;

      if (!pages.length || apiPages.length >= apiTotal) {
        break;
      }

      apiPage += 1;
    }

    const pages = matched.filter((page) => page.type === 'page');
    const services = matched.filter((page) => page.type === 'service');

    return {
      pages: pages.slice(0, MAX_PAGES),
      total_page: pages.length,
      services: services.slice(0, MAX_PAGES),
      total_service: services.length,
    };
  }

  async fetchSearchResults(query) {
    const shop = this.getShopDomain();
    const productsUrl = `${getBoostSearchBaseUrl()}?${this.buildProductParams(query, 1)}`;

    if (!shop || !query) {
      warnBoostHeaderSearch('fetchSearchResults aborted', {
        reason: !shop ? 'missing shop domain' : 'missing query',
        shop,
        query,
        boostPFSConfig: getBoostConfig(),
      });
      return;
    }

    this.searchAbort?.abort();
    this.searchAbort = new AbortController();
    const requestId = ++this.searchRequestId;

    this.drawers.forEach((drawer) => drawer.classList.add('is-loading'));

    try {
      const [productsResponse, pagesData] = await Promise.all([
        fetch(productsUrl, {
          signal: this.searchAbort.signal,
        }),
        this.fetchBoostPages(query, this.searchAbort.signal),
      ]);

      const productsData = await productsResponse.json();

      if (requestId !== this.searchRequestId) {
        return;
      }

      this.drawers.forEach((drawer) => {
        if (!this.isDrawerReady(drawer)) {
          warnBoostHeaderSearch('Drawer not ready, skipping render', {
            drawerId: drawer.id,
            refs: this.prepareDrawer(drawer),
          });
          return;
        }

        this.prepareDrawer(drawer);
        drawer._productState.query = query;
        drawer._productState.page = 1;
        const productResult = this.renderProducts(drawer, productsData, { append: false });
        this.renderPages(drawer, pagesData, query);
        this.showResultsMode(drawer);

        if (!productResult?.hasProducts) {
          const refs = this.prepareDrawer(drawer);
          this.setEmptyMessage(refs, {
            visible: true,
            text: drawer._config.emptyMessage,
          });
        }
      });
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }

      console.error('[boost-header-search]', error);
    } finally {
      this.drawers.forEach((drawer) => drawer.classList.remove('is-loading'));
    }
  }

  async loadMoreProducts(drawer) {
    const state = drawer._productState;
    const refs = this.prepareDrawer(drawer);

    if (!state?.query || state.loading || !state.hasMore || !refs?.productsList) {
      return;
    }

    state.loading = true;
    refs.sentinel?.removeAttribute('hidden');

    try {
      const nextPage = state.page + 1;
      const data = await fetch(
        `${getBoostSearchBaseUrl()}?${this.buildProductParams(state.query, nextPage)}`
      ).then((response) => response.json());

      state.page = nextPage;
      this.renderProducts(drawer, data, { append: true });
    } catch (error) {
      console.error('[boost-header-search]', error);
    } finally {
      state.loading = false;
    }
  }

  renderProducts(drawer, data, { append = false } = {}) {
    const refs = this.prepareDrawer(drawer);
    if (!refs?.productsList) {
      warnBoostHeaderSearch('renderProducts skipped: missing productsList', {
        drawerId: drawer.id,
        refs,
      });
      return { hasProducts: false };
    }

    const { productsList, productsLink, sentinel } = refs;
    const state = drawer._productState;
    const products = (data.products || []).slice(0, PRODUCT_BATCH);
    const total = data.total_product ?? products.length;

    state.total = total;
    state.hasMore = false;

    if (!append) {
      productsList.innerHTML = '';
    }

    if (!products.length) {
      if (!append) {
        productsList.innerHTML = '';
      }
      productsLink?.setAttribute('hidden', '');
      sentinel?.setAttribute('hidden', '');
      return { hasProducts: false };
    }

    const requestId = data.meta?.rid || '';
    const markup = products
      .map((product) => this.renderProductCard(product, drawer._config, state.query, requestId))
      .join('');
    productsList.insertAdjacentHTML('beforeend', markup);
    this.setEmptyMessage(refs, { visible: false });

    this.updateViewAllLink(productsLink, drawer._config, state.query, total);
    sentinel?.setAttribute('hidden', '');
    return { hasProducts: true };
  }

  renderPages(drawer, data, query) {
    const refs = this.prepareDrawer(drawer);
    if (!refs?.pagesList && !refs?.servicesList) {
      warnBoostHeaderSearch('renderPages skipped: missing pagesList/servicesList', {
        drawerId: drawer.id,
        refs,
      });
      return;
    }

    const {
      pagesSection,
      pagesList,
      pagesLink,
      servicesSection,
      servicesList,
      servicesLink,
    } = refs;
    const pages = data.pages || [];
    const services = data.services || [];

    this.renderTypedPageSection({
      section: pagesSection,
      list: pagesList,
      link: pagesLink,
      items: pages,
      total: data.total_page ?? pages.length,
      query,
      config: drawer._config,
      linkType: 'pages',
    });

    this.renderTypedPageSection({
      section: servicesSection,
      list: servicesList,
      link: servicesLink,
      items: services,
      total: data.total_service ?? services.length,
      query,
      config: drawer._config,
      linkType: 'pages-services',
    });
  }

  renderTypedPageSection({ section, list, link, items, total, query, config, linkType }) {
    if (!list) return;

    if (!items.length) {
      section?.setAttribute('hidden', '');
      list.innerHTML = '';
      link?.setAttribute('hidden', '');
      return;
    }

    section?.removeAttribute('hidden');
    list.innerHTML = items.map((page) => this.renderPageItem(page, config)).join('');
    this.updateViewAllLink(link, config, query, total, linkType);
  }

  filterPages(pages) {
    const configById = new Map(
      this.getBoostPageEntries().map((entry) => [Number(entry.id), entry])
    );

    if (!configById.size) {
      return [];
    }

    return pages.reduce((matched, page) => {
      if (page?.id == null) return matched;

      const entry = configById.get(Number(page.id));
      if (!entry) return matched;

      matched.push({
        ...page,
        type: entry.type,
        image: entry.image || page.image,
      });

      return matched;
    }, []);
  }

  updateViewAllLink(link, config, query, total, type = null) {
    if (!link || total <= 0) {
      link?.setAttribute('hidden', '');
      return;
    }

    const url = new URL(config.searchUrl, window.location.origin);
    url.searchParams.set('q', query);

    if (type) {
      url.searchParams.set('type', type);
    }

    link.href = url.toString();
    link.textContent = config.viewAllLabel.replace('{count}', String(total));
    link.removeAttribute('hidden');
  }

  renderProductCard(product, config, query = '', requestId = '') {
    const imageInfo = product.images_info?.[0] || {};
    const image = imageInfo.src || product.images?.['1'] || '';
    const imageAlt =
      imageInfo.alt ||
      imageInfo.alt_text ||
      product.image_alt ||
      product.imageAlt ||
      product.featured_image?.alt ||
      product.title ||
      '';
    const displayAlt = this.cleanMediaAlt(imageAlt, product.title);
    const url = `${config.rootUrl && config.rootUrl !== '/' ? config.rootUrl : ''}/products/${product.handle}`;
    const price = this.formatPrice(product.price_min, product.price_max);
    const kiGeneratedBadge = this.renderKiGeneratedBadge(imageAlt, config.kiGeneratedLabel, true);
    let compareAtPrice = '';
    if (product.compare_at_price_max && product.compare_at_price_max > product.price_min) {
      compareAtPrice = this.formatPrice(product.compare_at_price_max, product.compare_at_price_max);
    }

    return `
      <li>
        <a
          class="boost-drawer__product-card"
          href="${this.escapeHtml(url)}"
          data-boost-product-id="${this.escapeHtml(product.id)}"
          data-boost-request-id="${this.escapeHtml(requestId)}"
          data-boost-query="${this.escapeHtml(query)}"
        >
          <div class="boost-drawer__product-media">
            ${
              image
                ? `<img src="${this.escapeHtml(image)}" alt="${this.escapeHtml(displayAlt)}" loading="lazy" width="600" height="600">${kiGeneratedBadge}`
                : ''
            }
          </div>
          <div class="boost-drawer__product-info">
            <p class="boost-drawer__product-title">${this.escapeHtml(product.title)}</p>
            <div class="boost-drawer__product-price-container">
              ${price ? `<div class="boost-drawer__product-price">${price}</div>` : ''}
              ${compareAtPrice != '' ? `<div class="boost-drawer__product-compare-price">${compareAtPrice}</div>` : ''}
            </div>
          </div>
        </a>
      </li>
    `;
  }

  renderKiGeneratedBadge(alt, label, compact = false) {
    const variant = this.getKiGeneratedVariant(alt);

    if (!label) return '';
    if (!variant) return '';

    const compactClass = compact ? ' ki-generated-badge--compact' : '';

    return `<span class="ki-generated-badge ki-generated-badge--${variant}${compactClass}" data-ki-generated-badge data-ki-generated-variant="${variant}">
      <span class="ki-generated-badge__label">${this.escapeHtml(label)}</span>
    </span>`;
  }

  getKiGeneratedVariant(alt) {
    const normalizedAlt = String(alt || '').trim().toLowerCase();

    if (normalizedAlt.includes('ai generated')) return 'video';
    if (normalizedAlt.includes('ai black')) return 'light';
    if (normalizedAlt.includes('ai white')) return 'dark';

    return '';
  }

  cleanMediaAlt(alt, fallback = '') {
    const cleaned = String(alt || '')
      .replace(/ai generated/gi, '')
      .replace(/ai black/gi, '')
      .replace(/ai white/gi, '')
      .replace(/\s+/g, ' ')
      .trim();

    return cleaned || fallback || '';
  }

  renderPageItem(page, config) {
    const url = `${config.rootUrl && config.rootUrl !== '/' ? config.rootUrl : ''}${page.url}`;

    return `
      <li class="boost-drawer__page-item">
        <a class="boost-drawer__page-link" href="${this.escapeHtml(url)}">
          <span class="boost-drawer__page-arrow" aria-hidden="true">
            <svg viewBox="0 0 14 17" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 8.5H13M13 8.5L7.5 3M13 8.5L7.5 14" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
          <span class="boost-drawer__page-title">${this.escapeHtml(page.title)}</span>
        </a>
      </li>
    `;
  }

  formatPrice(priceMin, priceMax) {
    if (priceMin == null) return '';

    const format = (amount) => {
      if (window.theme?.Currency?.formatMoney) {
        return theme.Currency.formatMoney(Math.round(amount * 100), theme.settings.moneyFormat);
      }

      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: this.currency || getBoostConfig().shop?.currency || 'EUR',
      }).format(amount);
    };

    if (priceMax != null && priceMax !== priceMin) {
      return `${format(priceMin)} – ${format(priceMax)}`;
    }

    return format(priceMin);
  }

  escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  animateIn(shell) {
    if (typeof Motion === 'undefined') {
      shell.style.visibility = 'visible';
      shell.style.transform = 'translateY(0)';
      return Promise.resolve();
    }

    return Motion.animate(
      shell,
      {
        visibility: 'visible',
        transform: ['translateY(-105%)', 'translateY(0)'],
      },
      {
        duration: this.animationDuration,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished;
  }

  animateOut(shell) {
    if (typeof Motion === 'undefined') {
      shell.style.visibility = 'hidden';
      shell.style.transform = 'translateY(-105%)';
      return Promise.resolve();
    }

    return Motion.animate(
      shell,
      {
        visibility: 'hidden',
        transform: 'translateY(-105%)',
      },
      {
        duration: this.animationDuration,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished;
  }

  onDocumentClick(event) {
    if (!this.isOpen) {
      return;
    }

    if (this.header.contains(event.target)) {
      return;
    }

    if ([...this.drawers].some((drawer) => drawer.contains(event.target))) {
      return;
    }

    this.deactivate();
  }

  onKeydown(event) {
    if (event.key !== 'Escape' || !this.isOpen) {
      return;
    }

    this.deactivate();
  }
}

function initBoostHeaderSearch() {

  document.querySelectorAll('.shopify-section').forEach((section) => {
    if (section.__boostHeaderSearch?.isInitialized) {
      return;
    }

    const hasBoostInput = Boolean(section.querySelector('.boost-input'));
    const hasDrawer = Boolean(section.querySelector('[data-boost-drawer]'));

    if (!hasBoostInput && !hasDrawer) {
      return;
    }

    const instance = new BoostHeaderSearch(section);
    if (instance.isInitialized) {
      section.__boostHeaderSearch = instance;
    } else {
      warnBoostHeaderSearch('Section init failed', { sectionId: section.id });
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBoostHeaderSearch);
} else {
  initBoostHeaderSearch();
}

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target;
  if (!section?.classList?.contains('shopify-section')) {
    return;
  }

  delete section.__boostHeaderSearch;
  initBoostHeaderSearch();
});
